import React, { useState, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Card, CardContent, Typography, Box, Grid, LinearProgress, makeStyles, Chip, Button, Tooltip, CircularProgress
} from '@material-ui/core';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import StorageIcon from '@material-ui/icons/Storage';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import ScheduleIcon from '@material-ui/icons/Schedule';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { getFirebaseUsageData } from '../services/firebaseUsageService';

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  quotaBox: {
    padding: '16px 20px',
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(248,250,252,0.8)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  quotaTitle: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  quotaNumbers: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 8,
  },
  remainingText: {
    fontSize: '1.4rem',
    fontWeight: 800,
    color: '#10b981',
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
  },
  limitSub: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(148,163,184,0.15)' : 'rgba(203,213,225,0.4)',
    marginTop: 4,
    marginBottom: 6,
  },
  progressFillGreen: {
    backgroundColor: '#10b981',
    borderRadius: 4,
  },
  progressFillYellow: {
    backgroundColor: '#f59e0b',
    borderRadius: 4,
  },
  infoFooter: {
    marginTop: 16,
    padding: '12px 16px',
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.04)',
    border: '1px solid rgba(16,185,129,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
}));

export default function FirebaseUsageSection() {
  const classes = useStyles();
  const theme = useTheme();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUsage = async () => {
    setLoading(true);
    const data = await getFirebaseUsageData();
    setUsage(data);
    setLoading(false);
  };

  useEffect(() => {
    loadUsage();
  }, []);

  if (loading && !usage) {
    return (
      <Card className={classes.card} elevation={0}>
        <CardContent style={{ padding: 32, textAlign: 'center' }}>
          <CircularProgress size={32} style={{ color: '#10b981' }} />
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
            Calculating Firebase Spark Free Quota usage...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!usage) return null;

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent style={{ padding: 24 }}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.titleGroup}>
            <CloudQueueIcon style={{ color: '#f59e0b', fontSize: 28 }} />
            <div>
              <Typography variant="h6" style={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                Firebase Spark Plan (Free Tier) Usage & Quota Tracker
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Live monitoring of daily & monthly free limits for Firestore, Cloud Storage & FCM
              </Typography>
            </div>
          </div>

          <Box display="flex" alignItems="center" style={{ gap: 8 }} flexWrap="wrap">
            <Chip
              icon={<CheckCircleOutlineIcon style={{ fontSize: 16, color: '#10b981' }} />}
              label="100% Free Plan Active (Zero Cost)"
              size="small"
              style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', fontWeight: 700 }}
            />
            <Chip
              icon={<ScheduleIcon style={{ fontSize: 14, color: '#94a3b8' }} />}
              label={usage.resetTimer}
              size="small"
              style={{ background: 'rgba(148,163,184,0.1)', color: '#94a3b8', fontSize: '0.72rem' }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={loadUsage}
              disabled={loading}
              startIcon={<RefreshIcon fontSize="small" />}
              style={{ borderRadius: 10, textTransform: 'none' }}
            >
              Refresh
            </Button>
          </Box>
        </div>

        {/* Quota Grid */}
        <Grid container spacing={2}>
          {/* 1. Daily Firestore Reads */}
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.quotaBox}>
              <div>
                <div className={classes.quotaTitle}>
                  <span>DAILY FIRESTORE READS</span>
                  <Tooltip title="Firebase Spark plan allows 50,000 free document reads every day. Quota resets at midnight PST.">
                    <InfoOutlinedIcon style={{ fontSize: 15, cursor: 'pointer', color: '#94a3b8' }} />
                  </Tooltip>
                </div>
                <div className={classes.quotaNumbers}>
                  <div>
                    <span className={classes.remainingText}>
                      {usage.readsRemainingToday.toLocaleString()}
                    </span>
                    <Typography variant="caption" display="block" style={{ color: '#10b981', fontWeight: 600 }}>
                      Reads Remaining Today
                    </Typography>
                  </div>
                </div>
              </div>
              <div>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, usage.readsPercentToday)}
                  className={classes.progressBar}
                  classes={{
                    bar: usage.readsPercentToday > 80 ? classes.progressFillYellow : classes.progressFillGreen,
                  }}
                />
                <div className={classes.limitSub} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Est. used today: ~{usage.estimatedDailyReads.toLocaleString()}</span>
                  <span>Limit: 50,000 / day</span>
                </div>
                <div className={classes.limitSub} style={{ marginTop: 2 }}>
                  <span>Monthly: ~{usage.readsRemainingMonthly.toLocaleString()} left of 1.5M</span>
                </div>
              </div>
            </div>
          </Grid>

          {/* 2. Daily Firestore Writes */}
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.quotaBox}>
              <div>
                <div className={classes.quotaTitle}>
                  <span>DAILY FIRESTORE WRITES</span>
                  <Tooltip title="Firebase Spark plan gives 20,000 free writes per day (user updates, history, bookmarks).">
                    <InfoOutlinedIcon style={{ fontSize: 15, cursor: 'pointer', color: '#94a3b8' }} />
                  </Tooltip>
                </div>
                <div className={classes.quotaNumbers}>
                  <div>
                    <span className={classes.remainingText}>
                      {usage.writesRemainingToday.toLocaleString()}
                    </span>
                    <Typography variant="caption" display="block" style={{ color: '#10b981', fontWeight: 600 }}>
                      Writes Remaining Today
                    </Typography>
                  </div>
                </div>
              </div>
              <div>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, usage.writesPercentToday)}
                  className={classes.progressBar}
                  classes={{
                    bar: usage.writesPercentToday > 80 ? classes.progressFillYellow : classes.progressFillGreen,
                  }}
                />
                <div className={classes.limitSub} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Est. used today: ~{usage.estimatedDailyWrites.toLocaleString()}</span>
                  <span>Limit: 20,000 / day</span>
                </div>
                <div className={classes.limitSub} style={{ marginTop: 2 }}>
                  <span>Monthly: ~{usage.writesRemainingMonthly.toLocaleString()} left of 600K</span>
                </div>
              </div>
            </div>
          </Grid>

          {/* 3. Firestore Stored Database */}
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.quotaBox}>
              <div>
                <div className={classes.quotaTitle}>
                  <span>FIRESTORE STORED DATA</span>
                  <Tooltip title="Firebase Spark plan provides 1 GB (1,024 MB) of free total stored Firestore document data.">
                    <StorageIcon style={{ fontSize: 15, cursor: 'pointer', color: '#94a3b8' }} />
                  </Tooltip>
                </div>
                <div className={classes.quotaNumbers}>
                  <div>
                    <span className={classes.remainingText}>
                      {usage.firestoreStorageRemainingMB} MB
                    </span>
                    <Typography variant="caption" display="block" style={{ color: '#10b981', fontWeight: 600 }}>
                      Database Storage Free
                    </Typography>
                  </div>
                </div>
              </div>
              <div>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, usage.firestoreStoragePercent)}
                  className={classes.progressBar}
                  classes={{
                    bar: classes.progressFillGreen,
                  }}
                />
                <div className={classes.limitSub} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Est. Database size: ~{usage.estimatedFirestoreDataMB} MB</span>
                  <span>Limit: 1,024 MB (1 GB)</span>
                </div>
                <div className={classes.limitSub} style={{ marginTop: 2 }}>
                  <span>Total documents: {usage.totalDocs.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </Grid>

          {/* 4. Cloud Storage (Images/Media) */}
          <Grid item xs={12} sm={6} md={3}>
            <div className={classes.quotaBox}>
              <div>
                <div className={classes.quotaTitle}>
                  <span>CLOUD STORAGE (MEDIA)</span>
                  <Tooltip title="Firebase gives 5 GB free file storage and 1 GB/day download bandwidth.">
                    <CloudQueueIcon style={{ fontSize: 15, cursor: 'pointer', color: '#94a3b8' }} />
                  </Tooltip>
                </div>
                <div className={classes.quotaNumbers}>
                  <div>
                    <span className={classes.remainingText}>
                      {(usage.cloudStorageRemainingMB / 1024).toFixed(2)} GB
                    </span>
                    <Typography variant="caption" display="block" style={{ color: '#10b981', fontWeight: 600 }}>
                      Cloud Storage Free
                    </Typography>
                  </div>
                </div>
              </div>
              <div>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, usage.cloudStoragePercent)}
                  className={classes.progressBar}
                  classes={{
                    bar: classes.progressFillGreen,
                  }}
                />
                <div className={classes.limitSub} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Used: ~{usage.estimatedCloudStorageMB} MB</span>
                  <span>Limit: 5,120 MB (5 GB)</span>
                </div>
                <div className={classes.limitSub} style={{ marginTop: 2 }}>
                  <span>Downloads: ~{(1024 - usage.estimatedDailyDownloadMB).toFixed(1)} MB left today</span>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>

        {/* Info Banner */}
        <div className={classes.infoFooter}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <span style={{ fontSize: '1rem' }}>💡</span>
            <Typography variant="caption" style={{ color: theme.palette.text.primary, fontWeight: 500 }}>
              <strong>Firebase Cloud Messaging (FCM)</strong> is 100% free and unlimited. Your current app usage is operating well within the Spark Free Tier limits.
            </Typography>
          </Box>
          <Typography variant="caption" style={{ color: '#10b981', fontWeight: 700 }}>
            Status: Optimal & Safe
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
}
